'use client';
import React from 'react';
import Link from 'next/link';
import { Sparkles, Briefcase, MapPin, Heart, Zap, Users, Globe, Star, Coffee } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import Icon from '@/components/ui/AppIcon';


const ICON_MAP: Record<string, React.ElementType> = {
  zap: Zap,
  heart: Heart,
  users: Users,
  globe: Globe,
  star: Star,
  coffee: Coffee,
};

export default function CareersContent() {
  const { t } = useLanguage();
  const cp = t?.careersPage;
  const values = cp?.values ?? [];
  const perks = cp?.perks ?? [];

  return (
    <main className="min-h-screen bg-background pt-24 pb-20">
      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-16">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
            <Briefcase size={13} className="text-primary" />
            <span className="text-xs font-600 text-primary">{cp?.badge}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-800 text-foreground mb-5 leading-tight">
            {cp?.titleBefore}{' '}
            <span className="gradient-text">{cp?.titleHighlight}</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">{cp?.intro}</p>
        </div>
      </section>

      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-16">
        <div className="glass-card rounded-2xl p-8 md:p-10 border border-border">
          <div className="max-w-3xl">
            <p className="text-xs font-600 uppercase tracking-widest text-primary mb-4">
              {cp?.missionLabel}
            </p>
            <h2 className="text-2xl md:text-3xl font-700 text-foreground mb-5 leading-snug">
              {cp?.missionTitle}
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">{cp?.missionP1}</p>
            <p className="text-muted-foreground leading-relaxed mb-4">{cp?.missionP2}</p>
            <p className="text-muted-foreground leading-relaxed">{cp?.missionP3}</p>
          </div>
        </div>
      </section>

      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-16">
        <h2 className="text-xl font-700 text-foreground mb-2">{cp?.valuesTitle}</h2>
        <p className="text-muted-foreground text-sm mb-6">{cp?.valuesSubtitle}</p>
        <div className="grid md:grid-cols-3 gap-5">
          {values.map((v, i) => {
            const Icon = ICON_MAP[v.icon] || Zap;
            return (
              <div key={`value-${i}`} className="glass-card rounded-xl p-6 border border-border">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Icon size={18} className="text-primary" />
                </div>
                <h3 className="text-sm font-700 text-foreground mb-1.5">{v.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-16">
        <h2 className="text-xl font-700 text-foreground mb-6">{cp?.perksTitle}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {perks.map((perk, i) => (
            <div
              key={`perk-${i}`}
              className="flex items-center gap-3 glass-card rounded-xl px-5 py-4 border border-border"
            >
              <div className="w-2 h-2 rounded-full bg-primary shrink-0" />
              <span className="text-sm text-foreground font-500">{perk}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-screen-xl mx-auto px-6 lg:px-10 mb-16">
        <h2 className="text-xl font-700 text-foreground mb-2">{cp?.openingsTitle}</h2>
        <p className="text-muted-foreground text-sm mb-6">{cp?.openingsSubtitle}</p>
        <div className="glass-card rounded-2xl border border-border p-10 text-center">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Briefcase size={20} className="text-muted-foreground" />
          </div>
          <h3 className="text-base font-700 text-foreground mb-2">{cp?.noOpeningsTitle}</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
            {cp?.noOpeningsDesc}
          </p>
          <p className="text-xs text-muted-foreground mt-3">{cp?.noOpeningsNote}</p>
        </div>
      </section>

      <section className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <div className="rounded-2xl bg-muted/50 border border-border p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={16} className="text-primary" />
              <h2 className="text-base font-700 text-foreground">{cp?.ctaTitle}</h2>
            </div>
            <p className="text-sm text-muted-foreground max-w-lg">{cp?.ctaDesc}</p>
          </div>
          <Link
            href="/contact"
            className="btn-primary px-6 py-2.5 rounded-xl text-sm inline-flex items-center gap-2 shrink-0"
          >
            <MapPin size={14} /> {cp?.ctaButton}
          </Link>
        </div>
      </section>
    </main>
  );
}
